import React, { useState, useRef, useEffect } from 'react';
import { showsAPI } from '../../lib/api';
import { useShowStore } from '../../stores/showStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { useConnectionStore } from '../../stores/connectionStore';
import { useClockTick } from '../../contexts/ClockTickContext.jsx';
import EditLockButton from './EditLockButton';
import { useAuthStore } from '../../stores/authStore';

const VIEWS = [
  { id: 'producer', label: 'Producer', key: '1' },
  { id: 'td', label: 'TD', key: '2' },
  { id: 'graphics', label: 'Graphics', key: '3' },
  { id: 'commentary', label: 'Commentary', key: '4' },
  { id: 'raw', label: 'Raw', key: '5' },
  { id: 'singular', label: 'Singular', key: '6' },
];

function pad2(n) { return String(n).padStart(2, '0'); }

// Wall clock (HH:MM:SS) ticking off the shared 1s context.
function TimeOfDay() {
  useClockTick();
  const d = new Date();
  return (
    <span style={{ fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace", fontSize: 22, fontWeight: 400, letterSpacing: '.02em', color: 'var(--text)' }}>
      {pad2(d.getHours())}:{pad2(d.getMinutes())}:{pad2(d.getSeconds())}
    </span>
  );
}

// Elapsed since the on-air cue was taken (+MM:SS, green) — mirrors the standard
// header's SEG ELAPSED. Ticks off the shared context.
function SegElapsed() {
  useClockTick();
  const onAirCue = useShowStore(s => s.cues.find(c => c.status === 'on_air') || null);
  const onAirStartTime = useShowStore(s => s.onAirStartTime);
  if (!onAirCue || !onAirStartTime) return null;
  const secs = Math.max(0, Math.floor((Date.now() - onAirStartTime) / 1000));
  const m = Math.floor(secs / 60), s = secs % 60;
  return (
    <span style={{ fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace", fontSize: 14, fontWeight: 400, color: 'var(--green)' }}>
      +{pad2(m)}:{pad2(s)}
    </span>
  );
}

function HotkeyChip({ children }) {
  return (
    <span style={{
      fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace", fontSize: 10, color: 'var(--faint)',
      border: '1px solid var(--border2)', borderRadius: 3, padding: '1px 4px',
    }}>{children}</span>
  );
}

// A quiet lit/unlit pill (AUTO / GUARD / FOLLOW). `tone` sets the lit colour var.
function StatePill({ active, tone, children, onClick, title }) {
  const color = active ? `var(${tone})` : 'var(--faint)';
  return (
    <span
      onClick={onClick}
      title={title}
      style={{
        padding: '4px 8px', borderRadius: 5, fontSize: 10, fontWeight: 500, letterSpacing: '.06em',
        color, border: `1px solid ${active ? `var(${tone})` : 'var(--border)'}`,
        background: active ? `color-mix(in srgb, var(${tone}) 12%, transparent)` : 'transparent',
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}
    >{children}</span>
  );
}

export default function SubHeaderModern({
  show, showId, activeView, onViewChange, onExit, onOpenSettings,
  onTake, onSkip, onBack, onReset,
  canTake, autoMode, onToggleAutoMode, takeGuard, onToggleTakeGuard,
}) {
  const followOnAir = useSettingsStore(s => s.followOnAir);
  const toggleFollowOnAir = useSettingsStore(s => s.toggleFollowOnAir);
  const wsConnected = useConnectionStore(s => s.wsConnected);
  const vmixConnected = useConnectionStore(s => s.vmixConnected);
  const singularConnected = useConnectionStore(s => s.singularConnected);
  // Show settings are a production edit; a text-only guest still runs the show.
  const canProduce = useAuthStore(s => !!s.user?.capabilities?.includes('production:edit'));

  const isLive = show?.status === 'live';
  const isDraft = show?.status === 'draft' || show?.status === 'ready';

  const [viewOpen, setViewOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const viewRef = useRef(null);
  const moreRef = useRef(null);

  useEffect(() => {
    function onDoc(e) {
      if (viewRef.current && !viewRef.current.contains(e.target)) setViewOpen(false);
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  async function handleStartShow() {
    if (!confirm('Start show? This will set the show to LIVE and begin the clock.')) return;
    try { await showsAPI.start(showId); } catch (err) { console.error('Failed to start show:', err); }
  }
  async function handleEndShow() {
    setMoreOpen(false);
    if (!confirm('End show? This will mark the show as complete.')) return;
    try { await showsAPI.end(showId); } catch (err) { console.error('Failed to end show:', err); }
  }
  async function handleResetShow() {
    setMoreOpen(false);
    if (!confirm('Reset show? All cue statuses will be cleared and the show will return to Draft.')) return;
    try { await showsAPI.reset(showId); onReset?.(); } catch (err) { console.error('Failed to reset show:', err); }
  }

  const activeLabel = VIEWS.find(v => v.id === activeView)?.label || 'Producer';
  const dot = (on) => ({ width: 6, height: 6, borderRadius: '50%', background: on ? 'var(--green)' : 'var(--faint)' });
  const transportBtn = { display: 'flex', alignItems: 'center', gap: 6, color: 'var(--dim)', fontSize: 12, fontWeight: 500, letterSpacing: '0.06em', cursor: 'pointer', whiteSpace: 'nowrap', background: 'none', border: 'none', padding: 0 };
  const menuItem = { display: 'block', width: '100%', textAlign: 'left', padding: '8px 14px', fontSize: 13, color: 'var(--dim)', background: 'none', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap' };

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 20, height: 58, padding: '0 24px',
      background: 'var(--surface)', borderBottom: '1px solid rgb(var(--c-line-hard))', flexShrink: 0, position: 'relative', zIndex: 5,
    }}>
      {/* Left: show name · view switcher · connection dots */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
        <span style={{ fontWeight: 500, fontSize: 15, whiteSpace: 'nowrap', color: 'var(--text)' }}>{show?.name || 'Show'}</span>
        <span style={{ color: 'var(--faint)' }}>/</span>
        <div style={{ position: 'relative' }} ref={viewRef}>
          <button onClick={() => setViewOpen(v => !v)} style={{ ...transportBtn, fontSize: 14, letterSpacing: 'normal', color: 'var(--dim)' }}>
            {activeLabel} ▾
          </button>
          {viewOpen && (
            <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 6, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: 4, minWidth: 150, boxShadow: '0 8px 24px rgba(0,0,0,.4)', zIndex: 20 }}>
              {VIEWS.map(v => (
                <button
                  key={v.id}
                  onClick={() => { onViewChange(v.id); setViewOpen(false); }}
                  style={{ ...menuItem, borderRadius: 6, color: v.id === activeView ? 'var(--text)' : 'var(--dim)', background: v.id === activeView ? 'var(--surface3)' : 'transparent', display: 'flex', justifyContent: 'space-between', gap: 16 }}
                >
                  <span>{v.label}</span><span style={{ color: 'var(--faint)', fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace" }}>{v.key}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <span style={{ display: 'flex', gap: 5, marginLeft: 6 }} title="WS · vMix · Singular">
          <span style={dot(wsConnected)} />
          <span style={dot(vmixConnected)} />
          <span style={dot(singularConnected)} />
        </span>
      </div>

      {/* Center: time of day + seg elapsed */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14 }}>
        <TimeOfDay />
        <SegElapsed />
      </div>

      {/* Right: transport */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, justifyContent: 'flex-end' }}>
        {isDraft && (
          <button onClick={handleStartShow} style={{ padding: '9px 18px', borderRadius: 8, background: 'var(--golive)', color: '#fff', fontWeight: 500, fontSize: 13, letterSpacing: '.04em', cursor: 'pointer', border: 'none', whiteSpace: 'nowrap' }}>GO LIVE</button>
        )}
        {/* data-lock-allow: the transport keeps working while the edit lock is on */}
        <button data-lock-allow onClick={onBack} style={transportBtn} title="Back (B)">BACK<HotkeyChip>B</HotkeyChip></button>
        <button data-lock-allow onClick={onSkip} style={transportBtn} title="Skip (S)">SKIP<HotkeyChip>S</HotkeyChip></button>
        <button
          data-lock-allow
          onClick={onTake}
          disabled={!canTake}
          style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '9px 26px', borderRadius: 8,
            background: canTake ? 'var(--live)' : 'var(--surface2)', color: canTake ? '#fff' : 'var(--faint)',
            fontWeight: 500, fontSize: 14, letterSpacing: '.06em', cursor: canTake ? 'pointer' : 'not-allowed',
            border: canTake ? 'none' : '1px solid var(--border)', boxShadow: canTake ? '0 1px 8px rgba(229,72,77,.3)' : 'none', whiteSpace: 'nowrap',
          }}
          title="Take (Space)"
        >
          TAKE<span style={{ fontFamily: "'Roboto Mono','Roboto Mono Fallback',monospace", fontSize: 10, opacity: .7, border: '1px solid rgba(255,255,255,.4)', borderRadius: 3, padding: '1px 5px' }}>␣</span>
        </button>
        <span style={{ display: 'flex', gap: 4 }}>
          <StatePill
            active={autoMode}
            tone="--green"
            onClick={onToggleAutoMode}
            title={isLive
              ? 'Auto mode: auto-advance cues based on in-time'
              : 'Auto mode needs the show to be live'}
          >AUTO</StatePill>
          <StatePill active={takeGuard} tone="--next" onClick={onToggleTakeGuard} title="Double-tap guard: ignore a second TAKE within 0.5s">GUARD</StatePill>
          <StatePill active={followOnAir} tone="--blue" onClick={toggleFollowOnAir} title="Follow live: auto-scroll to the on-air cue">FOLLOW</StatePill>
        </span>
        <div style={{ position: 'relative' }} ref={moreRef}>
          <button onClick={() => setMoreOpen(v => !v)} style={{ color: 'var(--faint)', fontWeight: 500, fontSize: 18, cursor: 'pointer', background: 'none', border: 'none', lineHeight: 1 }} title="More">⋯</button>
          {moreOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: 'var(--surface2)', border: '1px solid var(--border2)', borderRadius: 8, padding: 4, minWidth: 170, boxShadow: '0 8px 24px rgba(0,0,0,.4)', zIndex: 20 }}>
              {canProduce && <button onClick={() => { setMoreOpen(false); onOpenSettings?.(); }} style={{ ...menuItem, borderRadius: 6 }}>Settings</button>}
              <button onClick={handleResetShow} style={{ ...menuItem, borderRadius: 6 }}>Reset show</button>
              {isLive && <button onClick={handleEndShow} style={{ ...menuItem, borderRadius: 6 }}>End show</button>}
              <div style={{ borderTop: '1px solid var(--border)', margin: '4px 0' }} />
              <button onClick={() => { setMoreOpen(false); onExit?.(); }} style={{ ...menuItem, borderRadius: 6 }}>Back to dashboard</button>
            </div>
          )}
        </div>
        <EditLockButton />
      </div>
    </div>
  );
}
